import React, { useCallback, useEffect, useRef } from 'react';
import { useFragment, usePaginationFragment, useRelayEnvironment } from 'react-relay/hooks';

import { graphql } from 'babel-plugin-relay/macro';
import { useFilterMessagesContext } from 'contexts';
import { ChatMessagePatientForm, InfiniteScroll } from 'embeds/components';
import { concat, filter, find, forEach, get, isEmpty, last, map, reverse, size, uniq, uniqBy, uniqWith } from 'lodash';
import { commitLocalUpdate, ConnectionHandler, fetchQuery, RecordProxy } from 'relay-runtime';
import { useChatActivityReasonMany } from 'requests/resources';
import { LoggerStatusEnum, sendLog, TMessage, TUser } from 'ui-model';

import { usePrevious } from '@lib/hooks';
import { RolloutEnum } from '@lib/resources/types';
import {
  getLumaBotNotifyStaffMessageMetadata,
  getLumaBotNotifyStaffMessageMetadataV2,
} from '@lib/services/lumaBotService';

import usePermissions from 'hooks/usePermissions';
import useSocketEventsDebounced from 'hooks/useSocketEventsDebounced';
import { DcdThread, LumaBotThread } from 'screens/HubScreen/components';
import { toGlobalId } from 'services/relayService';

import { InfiniteScrollMessages_EmbedPatient$key } from './__generated__/InfiniteScrollMessages_EmbedPatient.graphql';
import { InfiniteScrollMessages_EmbedViewer$key } from './__generated__/InfiniteScrollMessages_EmbedViewer.graphql';
import { InfiniteScrollMessagesRefresh_EmbedQuery } from './__generated__/InfiniteScrollMessagesRefresh_EmbedQuery.graphql';
import ChatMessage from './ChatMessage';
import ChatMessageTyping from './ChatMessageTyping';
import { useFilterMessagesBySenderAndReceiver } from './hooks/useFilterMessagesBySenderAndReceiver';
import { getFilteredRetryMessages } from './utils/infiniteScrollMessages';

const DCD_THREAD_KEYS = [
  'interactiveVoiceResponse',
  'interactiveVoiceResponseDeflection',
  'patientReminder',
  'patientReminderConfirmationOnly',
  'patientApptOffer',
  'patientApptOfferWithReschedule',
  'patientReferralReminder',
  'patientReferralLastReminder',
  'patientOutreachReminder',
  'patientOutreachLastReminder',
  'patientOutboundReferralReminder',
];

const socketEvents = [
  'chat:client:inbound:message',
  'chat:server:inbound:message:replyToAutomated',
  'chat:server:outbound:message',
  'chat:server:outbound:message:automated',
  'chat:server:outbound:message:mention',
];

const queryRefresh = graphql`
  query InfiniteScrollMessagesRefresh_EmbedQuery($ids: [MongoID!]!) {
    messageByIds(_ids: $ids) {
      _id
      id
      oid_patient
      text
      type
      oid_facility
      oid_createdBy
      oid_recipient
      ...ChatMessage_EmbedMessage
    }
  }
`;

// eslint-disable-next-line
const filterDcdMessagesOut = <T,>(messages: T[]) =>
  uniqWith(messages, (first, next) => {
    const externalId = 'externalId.value';
    const firstExternalId = get(first, externalId);
    const nextExternalId = get(next, externalId);
    if (!firstExternalId || !nextExternalId) return false;
    return firstExternalId === nextExternalId;
  });

export interface InfiniteScrollMessagesProps {
  fragmentRefViewer: InfiniteScrollMessages_EmbedViewer$key;
  fragmentRef: InfiniteScrollMessages_EmbedPatient$key;
}

const InfiniteScrollMessages: React.FC<InfiniteScrollMessagesProps> = ({ fragmentRefViewer, fragmentRef }) => {
  const viewer = useFragment<InfiniteScrollMessages_EmbedViewer$key>(
    graphql`
      fragment InfiniteScrollMessages_EmbedViewer on User {
        ...ChatMessage_EmbedViewer
      }
    `,
    fragmentRefViewer,
  );

  const {
    data: { messagesConnection, ...patient },
    loadNext,
    isLoadingNext,
    hasNext,
  } = usePaginationFragment<any, InfiniteScrollMessages_EmbedPatient$key>(
    graphql`
      fragment InfiniteScrollMessages_EmbedPatient on Patient
        @argumentDefinitions(cursor: { type: "String" }, count: { type: "Int", defaultValue: 25 })
        @refetchable(queryName: "InfiniteScrollMessages_EmbedPatientRefetchable") {
        id
        _id
        ...ChatMessageTyping_EmbedNode

        # TODO: Message_Patient fragment
        # _id
        name
        firstname
        lastname
        master

        # TODO: Message_Patient/UserAvatar_Patient fragment
        # name
        avatar: oid_avatar
        messagesConnection(
          first: $count
          after: $cursor
          filter: { _operators: { visibility: { in: [public, internal] }, status: { ne: skipped } } }
        ) @connection(key: "InfiniteScrollMessages_messagesConnection", filters: []) {
          edges {
            node {
              _id
              oid_patient
              text
              type
              channel
              key
              externalId {
                value
              }
              externalStatus {
                reason
              }
              retry {
                originalMessage
                count
              }
              oid_facility
              oid_createdBy
              oid_recipient
              ...ChatMessage_EmbedMessage
            }
          }
        }
      }
    `,
    fragmentRef,
  );

  const { filters, selectedFacilitiesAndFacilitiesFromGroups } = useFilterMessagesContext();

  const environment = useRelayEnvironment();
  const { data: dataReasons } = useChatActivityReasonMany();
  const chatActivityReasons = get(dataReasons, 'response');

  const handleSocketEvents = useCallback(
    (array: any[]) => {
      const arrayFilteredByPatient = filter(array, { patient: patient._id });
      const arrayFiltered = arrayFilteredByPatient.filter((message) => message.ref !== 'chat-notification');
      const ids = uniq(concat(map(arrayFiltered, '_id')));
      if (isEmpty(ids)) {
        return;
      }
      const handleMessage = (message: any) => {
        commitLocalUpdate(environment, (store) => {
          const patientRecord = store.get(toGlobalId('Patient', message.oid_patient));
          const messageRecord = store.get(message.id);
          const connectionRecord =
            patientRecord &&
            ConnectionHandler.getConnection(patientRecord, 'InfiniteScrollMessages_messagesConnection');
          if (!connectionRecord || !messageRecord) {
            sendLog('Cannot refresh InfiniteScrollMessages', { patientRecord, messageRecord }, LoggerStatusEnum.warn);
            return;
          }
          // Invalidate connection just in case, to refetch next time it's rendered
          connectionRecord.invalidateRecord();

          // Check if the edge already exists
          const edge = find(connectionRecord.getLinkedRecords('edges'), (recordEdge) => {
            const node = recordEdge && recordEdge.getLinkedRecord('node');
            return node && message.id === node.getValue('fakeId');
          }) as RecordProxy<{}> | undefined;
          if (edge) {
            // Record already exists > update
            const node = edge.getLinkedRecord('node')!;
            node.copyFieldsFrom(messageRecord);
          } else {
            // Insert new message
            const newEdge = ConnectionHandler.createEdge(store, connectionRecord, messageRecord, 'MessageEdge');
            if (newEdge) {
              try {
                ConnectionHandler.insertEdgeBefore(connectionRecord, newEdge);
              } catch (error) {
                sendLog('Cannot insertEdgeBefore InfiniteScrollMessages', error, LoggerStatusEnum.error);
              }
            }
          }
        });
      };
      fetchQuery<InfiniteScrollMessagesRefresh_EmbedQuery>(environment, queryRefresh, { ids }).then((response) => {
        forEach(ids, (id) => {
          const message = find(response.messageByIds, { _id: id });
          if (message) {
            handleMessage(message);
          }
        });
      });
    },
    [environment, patient._id],
  );

  useSocketEventsDebounced(socketEvents, handleSocketEvents);

  // WORKAROUND: Remove filter once BE doesn't send additional messages for completed flows
  const allMessages = filter(
    uniqBy(reverse(map(get(messagesConnection, 'edges'), 'node')), '_id'),
    (message) => !/^Luma(b|B)ot (form|flow) was finished: /.test(message.text || ''),
  );

  const filteredMessagesByRetry = getFilteredRetryMessages(allMessages);
  const messages = filterDcdMessagesOut(
    useFilterMessagesBySenderAndReceiver(
      filteredMessagesByRetry,
      filters.me,
      selectedFacilitiesAndFacilitiesFromGroups,
    ),
  );

  const itemsRef = useRef<any>([]);
  const prevPatient = usePrevious(patient);
  const lastMessageHash = `${get(last(messages), '_id')}:${get(last(messages), 'text')}`;
  const prevLastMessageHash = usePrevious(lastMessageHash);
  const prevMessages = usePrevious(messages);

  useEffect(() => {
    itemsRef.current = itemsRef.current.slice(0, messages.length);
  }, [messages]);

  useEffect(() => {
    const messagesCount = size(messages);
    if (!messagesCount) {
      return;
    }
    if (get(patient, '_id') !== get(prevPatient, '_id')) {
      // New patient > scroll to bottom
      const messageDiv = itemsRef.current[messagesCount - 1];
      messageDiv.scrollIntoView({ behavior: 'instant' });
    } else if (lastMessageHash !== prevLastMessageHash) {
      // New message > scroll to bottom
      const messageDiv = itemsRef.current[messagesCount - 1];
      // Jump one cycle to ensure the message is rendered before scrolling down
      setTimeout(() => {
        messageDiv.scrollIntoView({ behavior: 'smooth' });
      });
    } else if (messagesCount > size(prevMessages)) {
      // More items loaded on top > scroll to current item
      const index = messagesCount - size(prevMessages) - 1;
      const messageDiv = itemsRef.current[index];
      messageDiv?.scrollIntoView({ behavior: 'instant' });
    }
  }, [itemsRef, patient, prevPatient, lastMessageHash, prevLastMessageHash, messages, prevMessages]);

  const { hasAccessToProduct } = usePermissions();
  const isLumaBotV1 = hasAccessToProduct(RolloutEnum.LumaBotV1);

  return (
    <InfiniteScroll
      useWindow={false}
      initialLoad={false}
      isReverse
      hasMore={hasNext}
      loadMore={() => {
        if (!isLoadingNext) {
          loadNext(10);
        }
      }}
    >
      {/* @ts-ignore */}
      {map(filter(messages), ({ retriedChannels, key, ...message }, index) => {
        // Handle notifyStaff messages
        const { flowId } = !isLumaBotV1
          ? getLumaBotNotifyStaffMessageMetadataV2(message.text || '')
          : getLumaBotNotifyStaffMessageMetadata(message.text || '');

        if (flowId) {
          if (!isLumaBotV1) {
            return (
              <React.Fragment key={index}>
                <LumaBotThread patient={(patient as unknown) as TUser} patientFlowId={flowId} />
                <div
                  ref={(ref) => {
                    itemsRef.current[index] = ref;
                  }}
                />
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={index}>
              <ChatMessagePatientForm
                key={flowId}
                patientId={patient._id}
                resourceId={flowId}
                interlocutor={patient as any}
              />
              <div
                ref={(ref) => {
                  itemsRef.current[index] = ref;
                }}
              />
            </React.Fragment>
          );
        }

        if (key && DCD_THREAD_KEYS.includes(key)) {
          return (
            <React.Fragment key={index}>
              <DcdThread
                patientId={patient._id}
                externalId={get(message, 'externalId.value')}
                retriedChannels={retriedChannels}
                chatActivityReasons={chatActivityReasons}
                originalMessage={(message as unknown) as TMessage}
              />
              <div
                ref={(ref) => {
                  itemsRef.current[index] = ref;
                }}
              />
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={index}>
            <ChatMessage
              /* @ts-ignore */
              retriedChannels={retriedChannels}
              fragmentRefViewer={viewer}
              /* @ts-ignore */
              fragmentRef={message}
              chatActivityReasons={chatActivityReasons}
            />
            <div
              ref={(ref) => {
                itemsRef.current[index] = ref;
              }}
            />
          </React.Fragment>
        );
      })}
      <ChatMessageTyping fragmentRef={patient} />
    </InfiniteScroll>
  );
};

export default InfiniteScrollMessages;
